import {
  BUSINESS_KINDS,
  type BusinessKind,
  CONSENT_BASIS_HINTS,
  IMPORT_CONSENT_BASES,
  IMPORT_DUPLICATE_POLICIES,
  IMPORT_DUPLICATE_POLICY_LABELS,
  type ImportConsentBasis,
  type UpdateImportBody,
} from '@hearth/shared';
import { useId } from 'react';
import { Card, CardBody, CardHeader, Field, Input, MultiSelect, Select } from '../../components/ui';
import { useLookups } from '../../hooks/useMeta';
import { kindPluralLabel } from '../leads/model';

/** Settings applied to every row of an import (one file, or every file of a folder scan). */
export type ImportOptions = Pick<UpdateImportBody, 'defaultOwnerId' | 'defaultSourceId' | 'defaultTagIds' | 'defaultCountry' | 'duplicatePolicy'> & {
  defaultConsentBasis: ImportConsentBasis;
  /** Which list (Leads, Dealers, Reps) the leads the file creates go to. */
  businessKind: BusinessKind;
};

/** Two letters after trimming, as the server expects. */
export function validCountry(country: string): boolean {
  return /^[a-z]{2}$/i.test(country.trim());
}

/**
 * "For every row", "Email consent" and "When a row matches an existing lead": the import settings shared by the mapping
 * step and the folder scan. `source` shows the lead source picker (a folder scan keeps each file's own source).
 */
export function ImportOptionCards({ value, onChange, source = true }: { value: ImportOptions; onChange: (patch: Partial<ImportOptions>) => void; source?: boolean }) {
  const { meta } = useLookups();
  const policyName = useId();
  const consent = value.defaultConsentBasis;
  return (
    <>
      <Card>
        <CardHeader title="For every row" />
        <CardBody className="space-y-3">
          <Field label="These businesses are" hint="New leads from this file go to that list. Businesses already in Hearth stay where they are.">
            <Select
              value={value.businessKind}
              onChange={(event) => onChange({ businessKind: BUSINESS_KINDS.find((kind) => kind === event.target.value) ?? 'pizzeria' })}
              options={BUSINESS_KINDS.map((kind) => ({ value: kind, label: kindPluralLabel(kind) }))}
            />
          </Field>
          <Field label="Salesperson" hint="Rows with a salesperson column use that instead.">
            <Select value={value.defaultOwnerId ?? ''} onChange={(event) => onChange({ defaultOwnerId: event.target.value || null })}>
              <option value="">Unassigned (territory rules apply)</option>
              {(meta?.users ?? []).filter((u) => u.isActive).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Field>
          {source && (
            <Field label="Lead source">
              <Select value={value.defaultSourceId ?? ''} onChange={(event) => onChange({ defaultSourceId: event.target.value || null })}>
                <option value="">None</option>
                {(meta?.sources ?? []).filter((s) => s.isActive).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Tags">
            <MultiSelect
              label="Tags"
              options={(meta?.tags ?? []).map((tag) => ({ value: tag.id, label: tag.name }))}
              value={value.defaultTagIds}
              onChange={(defaultTagIds) => onChange({ defaultTagIds })}
            />
          </Field>
          <Field
            label="Country when a row has none"
            hint="Two-letter code, e.g. CA or US."
            error={validCountry(value.defaultCountry) ? undefined : 'Enter a two-letter country code.'}
          >
            <Input value={value.defaultCountry} maxLength={2} onChange={(event) => onChange({ defaultCountry: event.target.value.toUpperCase() })} />
          </Field>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Email consent" />
        <CardBody>
          <Field
            label="Where did these email addresses come from?"
            required
            hint={
              consent === 'none'
                ? 'Bulk email skips these contacts until consent is recorded (Canada’s anti-spam law, CASL). You can record it later from the leads table.'
                : 'Recorded on every contact this import adds, with the file name and today’s date. Existing contacts keep what they have.'
            }
          >
            <Select
              value={consent}
              title={CONSENT_BASIS_HINTS[consent]}
              onChange={(event) => onChange({ defaultConsentBasis: IMPORT_CONSENT_BASES.find((basis) => basis === event.target.value) ?? 'none' })}
            >
              {IMPORT_CONSENT_BASES.map((basis) => (
                <option key={basis} value={basis}>
                  {CONSENT_BASIS_HINTS[basis]}
                </option>
              ))}
            </Select>
          </Field>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="When a row matches an existing lead" />
        <CardBody className="space-y-2 text-[13px]">
          {IMPORT_DUPLICATE_POLICIES.map((option) => (
            <label key={option} className="flex items-start gap-2">
              <input type="radio" name={policyName} className="mt-0.5" checked={value.duplicatePolicy === option} onChange={() => onChange({ duplicatePolicy: option })} />
              {IMPORT_DUPLICATE_POLICY_LABELS[option]}
            </label>
          ))}
        </CardBody>
      </Card>
    </>
  );
}
